import { Button, Card, H4, ProgressBar, Spinner } from "@blueprintjs/core";
import classnames from "classnames";
import { getQueueProjectsExportColumnsForRegion } from "components/sidebar/queueProjects/queueProjectsExportColumn";
import { useBusPageConfiguration } from "contexts/BusPageContext/BusPageConfigurationState";
import { loader } from "graphql.macro";
import { getHasuraDataAndConvertToNiraType } from "graphql/helpers/queryHelpers";
import React, { useLayoutEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Loading } from "types/loadingType";
import {
    convertHasuraQueueProjectsToNiraQueueProjects,
    QueueProject
} from "types/queueProjectsType";
import { RegionType } from "types/regionType";
import { getCapacityCostsScopeViewFromRegionAndGenerator } from "types/scopeViewType";
import { downloadJSONToCSV } from "utils/csv";
import {
    computeQueueProjectStats,
    queueProjectsToExportObject
} from "utils/queueProjectUtils";
import "./BusScopeAssumptionsSection.scss";

const GET_QUEUE_PROJECTS = loader("src/graphql/getQueueProjects.graphql");

const BusScopeAssumptionsSection: React.FC = () => {
    const { bus, selectedScope, selectedGeneratorType } =
        useBusPageConfiguration();

    const scopeView = getCapacityCostsScopeViewFromRegionAndGenerator(
        bus.region,
        selectedGeneratorType
    );

    // Queried here (not in a child) so the hook runs unconditionally
    // before the no-scope early return below.
    const queueProjects: Loading<ReadonlyArray<QueueProject>> =
        getHasuraDataAndConvertToNiraType(
            GET_QUEUE_PROJECTS,
            convertHasuraQueueProjectsToNiraQueueProjects,
            {
                scope: selectedScope?.scope ?? "",
                scope_views: [scopeView],
                region: bus.region
            },
            selectedScope === undefined
        );

    if (!selectedScope) {
        return null;
    }

    return (
        <div className="BusScopeAssumptionsSection">
            <h3 className="bp5-heading">
                &ldquo;{selectedScope.display_name}&rdquo; Assumptions
            </h3>
            <QueueProjectsStats queueProjects={queueProjects} />
            <DataFreshnessDescription
                markdown={selectedScope.data_freshness_markdown}
            />
            <QueueProjectsDownload
                queueProjects={queueProjects}
                region={bus.region}
                scopeLabel={selectedScope.display_name}
            />
        </div>
    );
};

const QueueProjectsStats: React.FC<{
    readonly queueProjects: Loading<ReadonlyArray<QueueProject>>;
}> = ({ queueProjects }) => {
    if (queueProjects === "loading") {
        return <Spinner size={20} />;
    }

    if (queueProjects.length === 0) {
        return null;
    }

    const stats = computeQueueProjectStats(queueProjects);

    return (
        <div className="BusScopeAssumptionsSection-stats">
            <AssumptionStatCard
                label="Projects included"
                value={`${stats.includedCount}`}
                total={`${stats.totalCount}`}
                fraction={safeFraction(stats.includedCount, stats.totalCount)}
            />
            <AssumptionStatCard
                label="Capacity included"
                value={`${stats.includedCapacityGw.toFixed(1)} GW`}
                total={`${stats.totalCapacityGw.toFixed(1)} GW`}
                fraction={safeFraction(
                    stats.includedCapacityGw,
                    stats.totalCapacityGw
                )}
                orange
            />
        </div>
    );
};

const safeFraction = (included: number, total: number): number =>
    total > 0 ? included / total : 0;

const AssumptionStatCard: React.FC<{
    readonly label: string;
    readonly value: string;
    readonly total: string;
    readonly fraction: number;
    readonly orange?: boolean;
}> = ({ label, value, total, fraction, orange }) => {
    return (
        <Card className="BusScopeAssumptionsSection-stat-card">
            <div className="bp5-text-muted bp5-text-small">{label}</div>
            <div className="BusScopeAssumptionsSection-stat-value-row">
                <H4 className="BusScopeAssumptionsSection-stat-value">
                    {value}
                </H4>
                <span className="bp5-text-muted bp5-text-small">/ {total}</span>
            </div>
            <ProgressBar
                value={fraction}
                animate={false}
                stripes={false}
                className={classnames("BusScopeAssumptionsSection-stat-bar", {
                    "BusScopeAssumptionsSection-stat-bar-orange": orange
                })}
            />
        </Card>
    );
};

const COLLAPSED_MAX_HEIGHT_PX = 250;

const DataFreshnessDescription: React.FC<{
    readonly markdown: string;
}> = ({ markdown }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);

    useLayoutEffect(() => {
        const element = contentRef.current;
        if (element === null) {
            return;
        }
        setContentHeight(element.scrollHeight);
    }, [markdown]);

    const isOverflowing = contentHeight > COLLAPSED_MAX_HEIGHT_PX + 1;
    const isCollapsed = isOverflowing && !isExpanded;

    return (
        <div className="BusScopeAssumptionsSection-description">
            <div className="BusScopeAssumptionsSection-description-wrapper">
                <div
                    ref={contentRef}
                    className="BusScopeAssumptionsSection-description-content"
                    style={{
                        maxHeight: isCollapsed
                            ? COLLAPSED_MAX_HEIGHT_PX
                            : contentHeight || undefined
                    }}
                >
                    <ReactMarkdown>{markdown}</ReactMarkdown>
                </div>
                {isCollapsed && (
                    <div className="BusScopeAssumptionsSection-fade" />
                )}
            </div>
            {isOverflowing && (
                <Button
                    minimal
                    small
                    className="BusScopeAssumptionsSection-toggle"
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    {isExpanded ? "Read less" : "Read more"}
                </Button>
            )}
        </div>
    );
};

const QueueProjectsDownload: React.FC<{
    readonly queueProjects: Loading<ReadonlyArray<QueueProject>>;
    readonly region: RegionType;
    readonly scopeLabel: string;
}> = ({ queueProjects, region, scopeLabel }) => {
    if (queueProjects === "loading" || queueProjects.length === 0) {
        return null;
    }

    const handleDownload = async () => {
        const exportColumns = getQueueProjectsExportColumnsForRegion(region);
        const exportObjects = queueProjectsToExportObject(
            queueProjects,
            exportColumns
        );
        await downloadJSONToCSV({
            json: exportObjects,
            filename: `Nira Queue Projects - ${region} - ${scopeLabel}.csv`
        });
    };

    return (
        <div className="BusScopeAssumptionsSection-download">
            <Button
                icon="download"
                text="Download queue projects (CSV)"
                onClick={handleDownload}
            />
        </div>
    );
};

export default BusScopeAssumptionsSection;
